import React, { useEffect, useRef, useState } from "react";
import { useMatch, useNavigate } from "react-router-dom";
import useDebounce from "../../helper/hooks/useDebounce";
import { useFetchCardLeads } from "../../tanstack queries/leadsQueries";
import DefaultProfilePicture from "../../common/defaultProfilePicture/DefaultProfilePicture";
import { useForm } from "react-hook-form";
import CardDataModal from "../../pages/connections/CardDataModal";
import { getAuthenticatedRequest } from "../../utils/API/api.ts";

const ScannedCardLeads = () => {
  const eventsId = useMatch("/attendee/:eventId/*");
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [leads, setLeads] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const loadMoreRef = useRef(null);
  const { register, watch, getValues, setValue, reset } = useForm();
  const [selectedLead, setSelectedLead] = useState(null);
  const [isCardModalOpen, setIsCardModalOpen] = useState(false);
  const debouncedSearchValue = useDebounce(searchValue, 300);

  const {
    data: cardLeadData,
    error: cardLeadError,
    loading,
  } = useFetchCardLeads({
    eventId: eventsId?.params?.eventId,
    page,
    searchValue: debouncedSearchValue,
  });

  const openCardModal = (lead) => {
    setSelectedLead(lead);
    Object.keys(lead).forEach((key) => setValue(key, lead[key]));
    setIsCardModalOpen(true);
  };

  const closeCardModal = () => {
    setSelectedLead(null);
    setIsCardModalOpen(false);
  };

  useEffect(() => {
    if (cardLeadData) {
      if (debouncedSearchValue) {
        setLeads(cardLeadData);
        setHasMore(false);
      } else {
        setLeads((prev) =>
          page === 1 ? cardLeadData : [...prev, ...cardLeadData]
        );

        setHasMore(cardLeadData.length === 10);
      }
    }

    if (cardLeadError) {
      console.error(cardLeadError);
    }
  }, [cardLeadData, cardLeadError, page, debouncedSearchValue]);

  useEffect(() => {
    setPage(1);
    setHasMore(true);
  }, [searchValue, eventsId.params.eventId]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (
          target.isIntersecting &&
          hasMore &&
          !loading &&
          !debouncedSearchValue
        ) {
          setPage((prev) => prev + 1);
        }
      },
      { threshold: 1 }
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) observer.observe(currentRef);

    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, [hasMore, loading, debouncedSearchValue]);

  const handleExport = async () => {
    const { data } = await getAuthenticatedRequest(
      `/attendee/getCardLeads?eventId=${eventsId?.params?.eventId}&limit=0`
    );
    if (!data?.cardLeads?.length) return alert("No data to export.");
    const headers = [
      "Name",
      "Mobile",
      "Email",
      "Company Name",
      "Role",
      "Address",
      "Website",
      "Meeting Time",
    ];
    const rows = data?.cardLeads.map((lead) => [
      lead.name || "N/A",
      lead.mobile || "N/A",
      lead.email || "N/A",
      lead.companyName || "N/A",
      lead.role || "N/A",
      lead.address || "N/A",
      lead.website || "N/A",
      lead.meetingTime
        ? new Date(lead.meetingTime).toLocaleString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
        : "N/A",
    ]);

    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers, ...rows].map((e) => e.join(",")).join("\n");

    const link = document.createElement("a");
    link.setAttribute("href", encodeURI(csvContent));
    link.setAttribute("download", "card_leads.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="w-full min-h-[90vh] bg-gray-50 md:ml-[17%] md:w-[83%] md:bg-white md:min-h-full">
      <div className="pt-[70px] pb-[70px] md:pt-0 md:mt-[70px]">
        <div className="w-full h-[60px] fixed justify-between top-0 bg-white flex items-center px-[16px] border-b border-[#EDEDED] z-20 md:mt-[59px] md:hidden">
          <div className="flex items-center">
            <img
              src="/svgs/Arrowleft.svg"
              className="w-[24px] h-[24px] object-cover cursor-pointer"
              alt="arrow"
              onClick={() => navigate("/events")}
            />
            <span className="ml-2 text-[22px] font-[500]">Card Leads</span>
          </div>

          <div
            className="bg-primary text-white px-2 py-1 text-[14px] ml-4 font-semibold rounded-[6px] inline-flex items-center text-center justify-center cursor-pointer hover:opacity-95 "
            onClick={handleExport}
          >
            Export
            <img
              src="/svgs/downloadIcon.svg"
              className="w-[17px] ml-2 font-[500]"
              alt=""
            />
          </div>
        </div>

        <div>
          <form
            className="flex items-center md:w-[400px] w-full justify-center bg-[#FAFAFA] mt-[20px] px-4"
            onSubmit={(e) => e.preventDefault()}
          >
            <div className="relative w-full mt-0">
              <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                <img src="/svgs/Search.svg" className="h-[19px]" alt="search" />
              </div>
              <input
                type="text"
                id="simple-search"
                className="bg-[#FAFAFA] border border-gray-300 text-gray-900 rounded-lg focus:ring-primary focus:border-primary block w-full pl-10 pr-10 pt-2 pb-2  dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary dark:focus:border-primary text-[14px]"
                autoComplete="off"
                required={true}
                value={searchValue}
                onChange={(e) => {
                  setSearchValue(e.target.value);
                  setPage(1);
                }}
                placeholder="Search card leads..."
              />
            </div>
          </form>
        </div>

        <div className="md:w-[100%] mymd:mt-[20px] mt-[20px] mb-[20px]">
          {loading ? (
            <div className="mx-4 md:w-[800px] gap-5 md:flex flex-wrap">
              {[1, 2, 3, 4, 5].map((num) => (
                <div
                  key={num}
                  className="bg-white mb-4 rounded-[10px] p-4 md:w-[350px] border border-[#EDEDED]"
                >
                  <div className="bg-gray-300 w-full h-[45px] rounded-[8px] animate-pulse"></div>
                  <div className="bg-gray-200 w-full mt-2 h-[32px] rounded-[8px] animate-pulse"></div>
                  <div className="bg-gray-300 w-full mt-2 h-[65px] rounded-[8px] animate-pulse"></div>
                </div>
              ))}
            </div>
          ) : leads?.length > 0 ? (
            <div className="md:flex flex flex-col md:flex-row flex-wrap gap-y-3 gap-x-10 px-3">
              {leads.map((lead, index) => {
                const [firstName = "N/A", ...rest] =
                  lead.name?.trim().split(" ") || [];
                const lastName = rest.length
                  ? rest[rest.length - 1]
                  : firstName;

                return (
                  <div
                    key={lead._id || index}
                    onClick={() => openCardModal(lead)}
                    className="bg-white rounded-[10px] px-[14px] py-[15px] md:w-[350px] border border-[#EDEDED] cursor-pointer transition"
                  >
                    <div className="flex gap-2 items-start">
                      <div className="min-w-[45px] max-w-[45px]">
                        <DefaultProfilePicture
                          firstName={firstName}
                          lastName={lastName}
                          style={{
                            width: "43px",
                            height: "43px",
                            borderRadius: "100%",
                            fontSize: "16px",
                          }}
                        />
                      </div>
                      <div className="flex justify-between w-full items-start gap-1.5">
                        <div className="flex flex-col justify-between w-full">
                          <div className="text-[15px] font-[500] md:font-semibold line-clamp-1">
                            {lead.name || "N/A"}
                          </div>
                          <div className="text-[14px] mt-0.5 font-normal text-gray-500 line-clamp-1">
                            {lead.role || "N/A"}
                          </div>
                          <div className="text-[13px] mt-0.5 font-normal text-gray-500 line-clamp-1">
                            {lead.companyName || "N/A"}
                          </div>
                        </div>
                      </div>
                    </div>
                    {lead.meetingTime && (
                      <div className="text-right mt-2">
                        <p className="text-green-600 text-[11px] bg-green-100 inline-flex px-2 py-0.5 rounded-[6px]">
                          {new Date(lead.meetingTime).toLocaleString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            timeZone: "Asia/Kolkata",
                          })}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="grid w-full place-items-center h-[250px] md:w-[65%]">
              <div>
                <img
                  src="/svgs/nullState.svg"
                  alt=""
                  className="w-[200px] h-[200px]"
                />
                <p className="text-[15px] font-medium text-[#717171] text-center">
                  Nothing here...
                </p>
              </div>
            </div>
          )}

          {cardLeadError && (
            <div className="text-red-600 mt-4 text-center w-full">
              {cardLeadError}
            </div>
          )}
        </div>
      </div>
      <CardDataModal
        cardData={selectedLead}
        triggerModal={isCardModalOpen}
        onClose={closeCardModal}
        register={register}
        watch={watch}
        getValues={getValues}
        setValue={setValue}
        attendeeId={watch("attendeeId")}
        eventsId={eventsId?.params?.eventId}
        reset={reset}
        edit={true}
      />
      <div ref={loadMoreRef}></div>
    </div>
  );
};

export default ScannedCardLeads;
